import React, { useState, useEffect } from 'react';

const CompanyInfoPage = ({ onSave, onCancel }) => {
  const [companyInfo, setCompanyInfo] = useState({
    name: '',
    street: '',
    number: '',
    block: '',
    staircase: '',
    floor: '',
    apartment: '',
    county: '',
    commercialRegister: '',
    fiscalCode: '',
    phone: '',
    representativeName: '',
    representativeEmail: '',
    representativePosition: ''
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/company-info', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCompanyInfo(data);
      }
    } catch (error) {
      console.error('Error fetching company info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCompanyInfo(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!companyInfo.name.trim()) {
      newErrors.name = 'Numele companiei este obligatoriu';
    }

    if (!companyInfo.fiscalCode.trim()) {
      newErrors.fiscalCode = 'Codul fiscal este obligatoriu';
    } else if (!/^(RO)?[0-9]{2,10}$/.test(companyInfo.fiscalCode.replace(/\s/g, ''))) {
      newErrors.fiscalCode = 'Format cod fiscal invalid';
    }

    if (companyInfo.representativeEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyInfo.representativeEmail)) {
      newErrors.representativeEmail = 'Format email invalid';
    }

    if (companyInfo.phone && !/^[\+]?[0-9\s\-\(\)]{8,20}$/.test(companyInfo.phone)) {
      newErrors.phone = 'Format telefon invalid';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/company-info', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(companyInfo),
      });

      if (response.ok) {
        const savedData = await response.json();
        alert('Informațiile companiei au fost salvate cu succes!');
        if (onSave) {
          onSave(savedData);
        }
      } else {
        throw new Error('Eroare la salvarea datelor');
      }
    } catch (error) {
      console.error('Error saving company info:', error);
      alert('Eroare la salvarea informațiilor companiei');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      window.history.back();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Se încarcă...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Informații Companie</h1>
              <p className="text-gray-600 mt-1">
                Configurați datele oficiale ale companiei dvs.
              </p>
            </div>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="space-y-6">
          {/* Informații de bază */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Informații de Bază</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nume Companie *
                </label>
                <input
                  type="text"
                  name="name"
                  value={companyInfo.name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="SC EXEMPLU SRL"
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cod Fiscal *
                </label>
                <input
                  type="text"
                  name="fiscalCode"
                  value={companyInfo.fiscalCode}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.fiscalCode ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="RO12345678"
                />
                {errors.fiscalCode && <p className="text-red-500 text-sm mt-1">{errors.fiscalCode}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Registrul Comerțului
                </label>
                <input
                  type="text"
                  name="commercialRegister"
                  value={companyInfo.commercialRegister}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="J40/1234/2020"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefon
                </label>
                <input
                  type="text"
                  name="phone"
                  value={companyInfo.phone}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="+40 21 123 4567"
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>
            </div>
          </div>

          {/* Adresă sediu */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Adresă Sediu</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Strada
                </label>
                <input
                  type="text"
                  name="street"
                  value={companyInfo.street}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Strada Exemplu"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Număr
                </label>
                <input
                  type="text"
                  name="number"
                  value={companyInfo.number}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="1A"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bloc
                </label>
                <input
                  type="text"
                  name="block"
                  value={companyInfo.block}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="A1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Scară
                </label>
                <input
                  type="text"
                  name="staircase"
                  value={companyInfo.staircase}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Etaj
                </label>
                <input
                  type="text"
                  name="floor"
                  value={companyInfo.floor}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Apartament
                </label>
                <input
                  type="text"
                  name="apartment"
                  value={companyInfo.apartment}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="12"
                />
              </div>

              <div className="md:col-span-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Județ
                </label>
                <input
                  type="text"
                  name="county"
                  value={companyInfo.county}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="București"
                />
              </div>
            </div>
          </div>

          {/* Reprezentant legal */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Reprezentant Legal</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nume Complet
                </label>
                <input
                  type="text"
                  name="representativeName"
                  value={companyInfo.representativeName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ion Popescu"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Funcție
                </label>
                <input
                  type="text"
                  name="representativePosition"
                  value={companyInfo.representativePosition}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Director General"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Reprezentant
                </label>
                <input
                  type="email"
                  name="representativeEmail"
                  value={companyInfo.representativeEmail}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.representativeEmail ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="ion.popescu@exemplu.ro"
                />
                {errors.representativeEmail && <p className="text-red-500 text-sm mt-1">{errors.representativeEmail}</p>}
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Preview</h2>
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="text-center mb-4">
                <h3 className="text-xl font-bold text-gray-900">
                  {companyInfo.name || 'NUME COMPANIE'}
                </h3>
                <p className="text-gray-600">
                  CIF: {companyInfo.fiscalCode || 'COD_FISCAL'}
                  {companyInfo.commercialRegister && ` • Reg. Com.: ${companyInfo.commercialRegister}`}
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Adresă:</h4>
                  <p className="text-gray-600 text-sm">
                    {[
                      companyInfo.street && `Str. ${companyInfo.street}`,
                      companyInfo.number && `nr. ${companyInfo.number}`,
                      companyInfo.block && `bl. ${companyInfo.block}`,
                      companyInfo.staircase && `sc. ${companyInfo.staircase}`,
                      companyInfo.floor && `et. ${companyInfo.floor}`,
                      companyInfo.apartment && `ap. ${companyInfo.apartment}`
                    ].filter(Boolean).join(', ') || 'Adresă necompletată'}
                    {companyInfo.county && <br />}{companyInfo.county}
                  </p>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Contact:</h4>
                  <p className="text-gray-600 text-sm">
                    {companyInfo.phone && <>Tel: {companyInfo.phone}<br /></>}
                    {companyInfo.representativeEmail && <>Email: {companyInfo.representativeEmail}</>}
                  </p>
                  
                  {companyInfo.representativeName && (
                    <>
                      <h4 className="font-medium text-gray-900 mb-2 mt-4">Reprezentant:</h4>
                      <p className="text-gray-600 text-sm">
                        {companyInfo.representativeName}
                        {companyInfo.representativePosition && <>, {companyInfo.representativePosition}</>}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Anulează
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {saving && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                )}
                {saving ? 'Se salvează...' : 'Salvează'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyInfoPage;